<template>
    <div class="my-auto text-white">
        <h4 class="text-info">{{ $t('mfa.approve.title') }}</h4>
        <template v-if="state === 'waiting'">
            <p class="text-white mb-3">{{ $t('mfa.approve.instruction') }}</p>
            <div class="text-center mb-3"><i class="mdi mdi-cellphone-key display-4 text-info"></i></div>
            <p class="text-white d-flex align-items-center justify-content-center gap-2 mb-1">
                <span class="spinner-border spinner-border-sm"></span>
                <span>{{ $t('mfa.approve.waiting') }}</span>
            </p>
            <p class="text-center mb-4"><small class="text-muted">{{ $t('mfa.approve.expiresIn', { time: remaining }) }}</small></p>
            <div class="d-grid mb-0 text-center">
                <button class="btn btn-outline-info" type="button" @click="fallback" :disabled="busy">{{ $t('mfa.approve.fallback') }}</button>
            </div>
        </template>
        <template v-else-if="state === 'completing'">
            <p class="text-white d-flex align-items-center justify-content-center gap-2">
                <span class="spinner-border spinner-border-sm"></span>
                <span>{{ $t('mfa.approve.approved') }}</span>
            </p>
        </template>
        <template v-else>
            <p class="text-white mb-4">{{ message }}</p>
            <div class="d-grid mb-0 text-center">
                <router-link to="/login" class="btn btn-info">{{ $t('mfa.approve.retry') }}</router-link>
            </div>
        </template>
        <div class="mt-3" v-if="errorMessage">
            <div class="error-msg-displayed">
                {{ errorMessage }}
            </div>
        </div>
    </div>
    <!-- Footer-->
    <footer class="footer footer-alt">
        <p class="text-white">{{ $t('logout.backTo') }} <router-link to="/login" class="text-sidebar-link ms-1"><b>{{ $t('common.backToLogin') }}</b></router-link></p>
    </footer>
</template>

<script>
// Second step of the sign-in when the account is protected by the approval
// on a phone: the person confirms with the biometrics in the Haxo Setup
// app, this page polls the request and opens the session once approved. A
// code by email stays possible when the phone is out of reach.
import { useUserStore } from '@/stores/userStore';
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const POLL_INTERVAL_MS = 2000;

export default {
    name: 'ApprovePage',
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const router = useRouter();

        const info = userStore.mfa_info;
        const challengeId = info?.challenge_id;
        const expiresAt = info?.expires_at ? new Date(info.expires_at) : null;

        // waiting | completing | denied | expired
        const state = ref('waiting');
        const message = ref('');
        const errorMessage = ref('');
        const remaining = ref('');
        const busy = ref(false);

        let pollTimer = null;
        let clockTimer = null;
        let polling = false;

        const stopTimers = () => {
            if (pollTimer) clearInterval(pollTimer);
            if (clockTimer) clearInterval(clockTimer);
            pollTimer = null;
            clockTimer = null;
        };

        const end = (newState, text) => {
            stopTimers();
            state.value = newState;
            message.value = text;
        };

        const updateRemaining = () => {
            if (!expiresAt) {
                remaining.value = '';
                return;
            }
            const seconds = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
            remaining.value = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
            if (seconds === 0 && state.value === 'waiting') {
                end('expired', t('mfa.approve.expired'));
            }
        };

        // The phone approved: the request id completes the sign-in like a code.
        const complete = async () => {
            end('completing', '');
            try {
                const path = await userStore.mfaLogin(challengeId);
                router.push({ path });
            } catch (error) {
                state.value = 'expired';
                message.value = t('mfa.approve.expired');
                errorMessage.value = t('mfa.approve.errors.failed');
            }
        };

        const poll = async () => {
            if (polling || state.value !== 'waiting') return;
            polling = true;
            try {
                const status = await userStore.loginChallengeStatus(challengeId);
                switch (status) {
                    case 'approved':
                        await complete();
                        break;
                    case 'denied':
                        end('denied', t('mfa.approve.denied'));
                        break;
                    case 'expired':
                    case 'consumed':
                        end('expired', t('mfa.approve.expired'));
                        break;
                    default:
                        break;
                }
            } catch (error) {
                // An unknown request is over; anything else (network) is retried.
                if (error.response?.status === 404) {
                    end('expired', t('mfa.approve.expired'));
                }
            } finally {
                polling = false;
            }
        };

        const fallback = async () => {
            busy.value = true;
            try {
                await userStore.loginChallengeFallback(challengeId);
                stopTimers();
                router.push({ path: '/mfa' });
            } catch (error) {
                if (error.response?.status === 410 || error.response?.status === 404) {
                    end('expired', t('mfa.approve.expired'));
                } else {
                    errorMessage.value = t('mfa.approve.errors.fallbackFailed');
                }
            } finally {
                busy.value = false;
            }
        };

        onMounted(() => {
            if (!challengeId) {
                router.replace({ path: '/login' });
                return;
            }
            updateRemaining();
            clockTimer = setInterval(updateRemaining, 1000);
            pollTimer = setInterval(poll, POLL_INTERVAL_MS);
        });

        onUnmounted(stopTimers);

        return {
            state,
            message,
            errorMessage,
            remaining,
            busy,
            fallback,
        };
    },
};
</script>
